<template>
  <PlanCard>
    <PlanContent>
      <template #title> <slot name="title" /> </template>

      <template #subtitle>
        <div class="w-full mt-2">
          <SelectInput
            id="select"
            name="volume-based-plan"
            :label="I18n.t('pricing.select_label')"
            :options="planOptions"
            :model-value="selectedOption"
            @update:modelValue="updateSelectedOption"
            controlled
          />
        </div>
      </template>

      <template #price>
        {{ selectedPlan.formattedPrice }}
      </template>

      <template #price-suffix>
        <p class="text-typo-tertiary m-2">/ {{ I18n.t("pricing.price_suffix") }}</p>
      </template>

      <template v-if="billingInterval === 'yearly'" #billing-appendix>
        {{ I18n.t("pricing.billed_yearly", { x: selectedPlan.formattedYearlyPrice }) }}
      </template>

      <template #action>
        <Button
          v-if="!isPlanChangeAvailable"
          :variant="buttonVariant"
          full-width
          @click="$emit('buy', selectedPlan)"
          :disabled="isDisabled"
          :aria-label="buttonText"
          :size="buttonSize"
        >
          {{ buttonText }}
        </Button>
        <Button
          v-else-if="isCurrentPlanYearly"
          :variant="buttonVariant"
          :disabled="isDisabled"
          full-width
          :aria-label="buttonText"
          :size="buttonSize"
          @click="$emit('alert')"
        >
          {{ buttonText }}
        </Button>
        <AjaxDialog
          v-else
          :url="`/switch_plan/${selectedPlan.id}`"
          :params="{ view: 'detailed' }"
          dialog-classes="max-w-3xl"
          content-selector="#switch-plan-popup"
          dialog-id="switch-plan-popup"
        >
          <template #trigger>
            <Button
              :variant="buttonVariant"
              :disabled="isDisabled"
              full-width
              :aria-label="buttonText"
              :size="buttonSize"
            >
              {{ buttonText }}
            </Button>
          </template>
        </AjaxDialog>
      </template>

      <template #plan-description>
        <slot name="plan-description" />
      </template>
    </PlanContent>
  </PlanCard>
</template>

<script setup lang="ts">
import { Button, SelectInput, SelectOption } from "prism";
import PlanCard from "./plan_card.vue";
import PlanContent from "./plan_content.vue";
import { BillingInterval, Plan } from "./types";
import { computed, inject, watch, ref } from "vue";
import type { I18n as I18nType } from "i18n-js";
import AjaxDialog from "../prism/ajax_dialog.vue";
import { usePlanButton } from "./plan_button_utils";
import { rbgPricingCardVolumeSelectedV100 } from "kaleido-event-tracker";
import { PricingPlanType } from "./events";
import { useMonitorSize } from "@/composables/monitor_size";

const props = defineProps<{
  plans: Plan[];
  currentPlan: Plan | null;
  billingInterval: BillingInterval;
  selectedVolume: number | null;
}>();

const emit = defineEmits<{
  (e: "buy", plan: Plan): void;
  (e: "alert"): void;
  (e: "volumeChanged", volume: number): void;
}>();

const I18n: I18nType = inject("I18n");
const { isSmallTablet } = useMonitorSize();

const buttonSize = computed(() => (!isSmallTablet.value ? "lg" : "default"));

const parsePlanToOption = (plan: Plan): SelectOption => ({
  label: `${plan.monthlyCredits.toLocaleString(I18n.locale)} ${
    plan.monthlyCredits > 1 ? I18n.t("pricing.credits") : I18n.t("pricing.credit")
  }`,
  secondaryLabel: `${plan.formattedPrice} / ${I18n.t("pricing.price_suffix")}`,
  value: `${plan.monthlyCredits}`,
});

const planOptions = computed(() => {
  const baseOptions = props.plans.map(parsePlanToOption);

  const enterpriseOption: SelectOption = {
    value: "enterprise",
    label: `100,000+ ${I18n.t("pricing.credits")} / ${I18n.t("pricing.year")}`,
    secondaryLabel: I18n.t("pricing.enterprise_solutions.lets_talk"),
  };

  return [...baseOptions, enterpriseOption];
});

const getInitialSelectedOption = (): SelectOption => {
  if (props.selectedVolume) {
    const matchingOption = planOptions.value.find((option) => Number(option.value) === props.selectedVolume);
    if (matchingOption) return matchingOption;
  }

  if (props.currentPlan && props.plans.some((plan) => plan.id === props.currentPlan?.id)) {
    return parsePlanToOption(props.currentPlan);
  }

  return planOptions.value[0];
};

const selectedOption = ref<SelectOption>(getInitialSelectedOption());
watch(
  () => props.selectedVolume,
  (newVolume) => {
    if (newVolume && Number(selectedOption.value.value) !== newVolume) {
      const matchingOption = planOptions.value.find((option) => Number(option.value) === newVolume);
      if (matchingOption) {
        selectedOption.value = matchingOption;
      }
    }
  }
);

const redirectToEnterpriseContact = () => {
  window.location.href = "/enterprise/contact";
};

const updateSelectedOption = (value: SelectOption) => {
  if (value.value === "enterprise") {
    redirectToEnterpriseContact();
    return;
  }

  rbgPricingCardVolumeSelectedV100({
    type: PricingPlanType.SubscribeVolume,
    credit_volume: Number(value.value),
    billing_interval: props.billingInterval,
  });

  selectedOption.value = value;

  emit("volumeChanged", Number(value.value));
};

const selectedPlan = computed(() => {
  return props.plans.find((plan) => plan.monthlyCredits === Number(selectedOption.value?.value));
});

const { isPlanChangeAvailable, buttonVariant, buttonText, isDisabled, isCurrentPlanYearly } = usePlanButton(
  selectedPlan,
  props.currentPlan
);
</script>
